import { useEffect, useRef, useState, type ReactNode } from "react";

/** Respecte prefers-reduced-motion (évalué côté client uniquement). */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return reduced;
}

/**
 * Fondu léger à l'entrée dans le viewport, via IntersectionObserver (sans librairie).
 * Si prefers-reduced-motion est actif, le contenu s'affiche directement.
 */
export function Reveal({ children, className = "" }: { children: ReactNode; className?: string }) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduced]);

  if (reduced) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={`transition-opacity duration-500 ease-out motion-reduce:transition-none ${
        visible ? "opacity-100" : "opacity-0"
      } ${className}`}
    >
      {children}
    </div>
  );
}

/** Échelle de bleus du site : du palier le plus clair au plus soutenu. */
const ACCENT_STEPS = [
  "var(--ocean-200)",
  "var(--ocean-300)",
  "var(--ocean-400)",
  "var(--ocean-500)",
  "var(--ocean-600)",
  "var(--ocean-700)",
] as const;

export function accentForIndex(index: number): string {
  return ACCENT_STEPS[((index % ACCENT_STEPS.length) + ACCENT_STEPS.length) % ACCENT_STEPS.length];
}

/** Décalage sous la barre de filtres collante (top-0, ~3.5rem de haut). */
const STICKY_OFFSET_PX = 56;

/** Sur mobile (< sm), le bandeau de filtres n'est plus collant : le header colle en haut. */
function useStickyOffset(): number {
  const [offset, setOffset] = useState(STICKY_OFFSET_PX);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const update = () => setOffset(mq.matches ? STICKY_OFFSET_PX : 0);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return offset;
}

/**
 * Section d'un média : en-tête collant sous la nav pendant que son contenu défile,
 * avec une fine barre de progression et un filet d'accent propre à sa position.
 */
export function MediaSection({
  id,
  className = "",
  accentIndex,
  eyebrow,
  title,
  place,
  period,
  intro,
  children,
}: {
  id: string;
  className?: string;
  accentIndex: number;
  eyebrow: string;
  title: string;
  place?: string;
  period: string;
  intro?: string;
  children: ReactNode;
}) {
  const reduced = useReducedMotion();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  const stickyOffset = useStickyOffset();
  const accent = accentForIndex(accentIndex);

  useEffect(() => {
    let frame = 0;
    const compute = () => {
      frame = 0;
      const el = contentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const anchor = stickyOffset + 120; // sous l'en-tête collant
      const scrolled = anchor - rect.top;
      const total = Math.max(rect.height - (window.innerHeight - anchor), 1);
      const next = Math.min(1, Math.max(0, scrolled / total));
      setProgress(next);
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [children, stickyOffset]);

  return (
    <div id={id} className={className}>
      <header
        className="sticky z-20 border-t border-border bg-background pt-8 pb-3 backdrop-blur supports-[backdrop-filter]:bg-background/95"
        style={{ top: `${stickyOffset}px` }}
      >
        <p className="eyebrow">{eyebrow}</p>
        <div className="mt-3 flex flex-wrap items-baseline justify-between gap-4">
          <h2 className="font-serif text-4xl leading-tight sm:text-5xl">{title}</h2>
          <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{period}</p>
        </div>
        {place && <p className="mt-2 text-sm italic text-muted-foreground">{place}</p>}
        {intro && (
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground/80">{intro}</p>
        )}
        <div
          className="mt-3 h-[3px] w-full overflow-hidden rounded-full bg-border/70"
          role="progressbar"
          aria-label={`Progression dans la section ${title}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
        >
          <div
            className={`h-full rounded-full ${reduced ? "" : "transition-[width] duration-150 ease-out"}`}
            style={{ width: `${progress * 100}%`, backgroundColor: accent }}
          />
        </div>
      </header>
      <div ref={contentRef}>{children}</div>
    </div>
  );
}

/** Variante hook du fondu, pour les éléments qui ne peuvent pas être enveloppés (ex: <li> d'une grille). */
export function useReveal<T extends HTMLElement>() {
  const reduced = useReducedMotion();
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduced]);

  const className = reduced
    ? ""
    : `transition-opacity duration-500 ease-out ${visible ? "opacity-100" : "opacity-0"}`;

  return { ref, className };
}
